import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  CdkFixedSizeVirtualScroll,
  CdkVirtualForOf,
  CdkVirtualScrollViewport,
} from '@angular/cdk/scrolling';
import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DOCUMENT,
  ElementRef,
  Injector,
  OnDestroy,
  TemplateRef,
  ViewContainerRef,
  afterNextRender,
  computed,
  contentChild,
  inject,
  input,
  linkedSignal,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { ScFieldHover } from '../fields/field-hover.directive';
import { UI_TEXT } from '../i18n';
import { snapToPixels } from '../overlay-snap';
import { matchesSearch } from '../text';
import { ScSelectItemDirective } from './select-item.directive';

/** Una opción de la lista, ya resuelta: su valor, su texto y si se puede elegir. */
interface Entry {
  id: string;
  item: any;
  value: any;
  label: string;
  disabled: boolean;
}

interface Group {
  /** Encabezado del grupo; null en una lista sin grupos. */
  key: string | null;
  entries: Entry[];
}

/** Alto de una opción (7 + 19 + 7, la de dx-list) y desde cuántas opciones la lista es virtual. */
const OPTION_HEIGHT = 33;
const VIRTUAL_FROM = 200;
/** Borde (1) y relleno (1 + 1) del panel: lo que queda del alto máximo para la lista. */
const PANEL_CHROME = 3;

let nextId = 0;

/**
 * Lista desplegable del kit (reemplaza a dx-select-box): un combobox de ARIA con la lista en un
 * overlay del CDK (`cdk-overlay-pane`, sobre las ventanas). Como la de DevExtreme:
 * - con la lista cerrada, ↓ y ↑ cambian el valor; Alt+↓ la abre; abierta, ↓ ↑ recorren las opciones,
 *   Enter y Tab eligen la enfocada y Escape cierra sin cambiar (y no cierra la ventana de atrás);
 * - con `searchable`, escribir filtra («contiene», sin mayúsculas ni tildes, también en grupos); al
 *   cerrar, el texto vuelve al del valor;
 * - el panel mide a lo más la mitad de la ventana, se abre hacia arriba si abajo no cabe y se cierra
 *   con un clic fuera o con el scroll de la página o de un contenedor.
 * Sólo avisa los cambios de la persona; `reset()` deja la lista vacía sin avisar.
 */
@Component({
  selector: 'sc-select',
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [ScFieldHover],
  imports: [NgTemplateOutlet, CdkVirtualScrollViewport, CdkFixedSizeVirtualScroll, CdkVirtualForOf],
  host: {
    class: 'sck-field sck-select',
    '[class.sck-field--disabled]': 'disabled()',
    '[class.sck-select--open]': 'isOpen()',
  },
  template: `
    <div class="sck-field__control">
      <input
        #field
        class="sck-field__input"
        type="text"
        role="combobox"
        autocomplete="off"
        aria-haspopup="listbox"
        [value]="inputText()"
        [readOnly]="!searchable()"
        [disabled]="disabled()"
        [attr.aria-label]="ariaLabel()"
        [attr.placeholder]="placeholder() || null"
        [attr.aria-expanded]="isOpen()"
        [attr.aria-controls]="isOpen() ? listId : null"
        [attr.aria-activedescendant]="activeId()"
        (click)="onFieldClick()"
        (input)="onType()"
        (keydown)="onKeydown($event)"
        (blur)="onBlur()"
      />
      @if (showPlaceholder()) {
        <div
          class="sck-field__placeholder"
          aria-hidden="true"
          [attr.data-placeholder]="placeholder()"
        ></div>
      }
    </div>
    @if (clearable() && selectedEntry()) {
      <button
        type="button"
        class="sck-field__clear"
        tabindex="-1"
        [attr.aria-label]="text.clear"
        (mousedown)="$event.preventDefault()"
        (click)="clear()"
      >
        <span class="material-icons sck-icon" aria-hidden="true" data-icon="cancel"></span>
      </button>
    }
    <button
      type="button"
      class="sck-field__button"
      tabindex="-1"
      aria-hidden="true"
      (mousedown)="$event.preventDefault()"
      (click)="toggle()"
    >
      <span class="sck-field__button-icon">
        <span class="material-icons sck-icon" aria-hidden="true" data-icon="arrow_drop_down"></span>
      </span>
    </button>

    <ng-template #panel>
      <div
        class="sck-select-panel"
        [class.sck-select-panel--above]="above()"
        (mousedown)="$event.preventDefault()"
      >
        @if (virtual()) {
          <cdk-virtual-scroll-viewport
            class="sck-select__list sck-select__viewport"
            role="listbox"
            [id]="listId"
            [attr.aria-label]="ariaLabel()"
            [itemSize]="optionHeight"
            [style.height.px]="viewportHeight()"
          >
            <ng-container *cdkVirtualFor="let entry of options(); trackBy: trackEntry">
              <ng-container *ngTemplateOutlet="option; context: { $implicit: entry }" />
            </ng-container>
          </cdk-virtual-scroll-viewport>
        } @else {
          <div class="sck-select-panel__scroll" [style.max-height.px]="maxListHeight()">
            <div
              class="sck-select__list"
              role="listbox"
              [id]="listId"
              [attr.aria-label]="ariaLabel()"
            >
              @for (group of groups(); track group.key) {
                @if (group.key === null) {
                  @for (entry of group.entries; track entry.id) {
                    <ng-container *ngTemplateOutlet="option; context: { $implicit: entry }" />
                  }
                } @else {
                  <div
                    class="sck-select__group"
                    role="group"
                    [attr.aria-labelledby]="listId + '-g' + $index"
                  >
                    <div class="sck-select__group-header" [id]="listId + '-g' + $index">
                      {{ group.key }}
                    </div>
                    @for (entry of group.entries; track entry.id) {
                      <ng-container *ngTemplateOutlet="option; context: { $implicit: entry }" />
                    }
                  </div>
                }
              }
            </div>
          </div>
        }
      </div>
    </ng-template>

    <ng-template #option let-entry>
      <div
        class="sck-select__option"
        role="option"
        [id]="entry.id"
        [class.sck-select__option--active]="showActive() && entry.id === activeId()"
        [class.sck-select__option--selected]="entry === selectedEntry()"
        [attr.aria-selected]="entry === selectedEntry()"
        [attr.aria-disabled]="entry.disabled ? 'true' : null"
        (click)="choose(entry)"
      >
        @if (itemTemplate(); as custom) {
          <ng-container *ngTemplateOutlet="custom.template; context: { $implicit: entry.item }" />
        } @else {
          {{ entry.label }}
        }
      </div>
    </ng-template>
  `,
})
export class ScSelectComponent implements OnDestroy {
  /** Los ítems, o con `grouped` los grupos `{ key, items }`. */
  readonly items = input<readonly any[] | null>(null);
  readonly grouped = input(false);
  /** Campo con el valor de cada ítem; sin él, el valor es el ítem. */
  readonly valueField = input<string | null>(null);
  /** Campo con el texto de cada ítem (o `label` como función); sin ninguno, el ítem como texto. */
  readonly labelField = input<string | null>(null);
  readonly label = input<((item: any) => string) | null>(null);
  readonly value = input<any>(null);
  readonly placeholder = input<string>(UI_TEXT.select);
  readonly searchable = input(false);
  readonly clearable = input(false);
  readonly disabled = input(false);
  readonly ariaLabel = input<string | null>(null);
  /** Avisa el valor elegido por la persona (nunca uno puesto por la pantalla ni `reset()`). */
  readonly valueChange = output<any>();

  protected readonly text = UI_TEXT;
  protected readonly optionHeight = OPTION_HEIGHT;
  protected readonly listId = `sck-select-list-${++nextId}`;
  protected readonly isOpen = signal(false);
  protected readonly above = signal(false);
  protected readonly maxListHeight = signal(0);
  protected readonly itemTemplate = contentChild(ScSelectItemDirective);

  /** El valor que se ve: el de la pantalla, hasta que la persona elige o se llama a `reset()`. */
  private readonly selected = linkedSignal(() => this.value());
  /** Lo escrito para buscar mientras la lista está abierta; null si no se está buscando. */
  private readonly search = signal<string | null>(null);
  private readonly activeIndex = signal(-1);
  /** Como dx-list: al abrir, la elegida no se pinta como enfocada hasta que se usan las flechas o se busca. */
  protected readonly showActive = signal(false);
  /** La persona borró el texto de una lista con búsqueda: al salir, como DevExtreme, queda sin valor. */
  private readonly emptied = signal(false);

  private readonly entries = computed<{ key: string | null; entries: Entry[] }[]>(() => {
    const raw = this.items() ?? [];
    const groups: { key: string | null; items: readonly any[] }[] = this.grouped()
      ? raw.map((group) => ({ key: String(group?.key ?? ''), items: group?.items ?? [] }))
      : [{ key: null, items: raw }];
    let index = 0;
    return groups.map((group) => ({
      key: group.key,
      entries: group.items.map((item) => ({
        id: `${this.listId}-${index++}`,
        item,
        value: this.valueOf(item),
        label: this.labelOf(item),
        disabled: item?.disabled === true,
      })),
    }));
  });

  protected readonly selectedEntry = computed(() => {
    const value = this.selected();
    if (value === null || value === undefined) return null;
    for (const group of this.entries())
      for (const entry of group.entries) if (entry.value === value) return entry;
    return null;
  });

  /** Los grupos que se ven: con búsqueda, sólo las opciones que coinciden (y sus grupos). */
  protected readonly groups = computed<Group[]>(() => {
    const term = this.search() ?? '';
    return this.entries()
      .map((group) => ({
        key: group.key,
        entries: term ? group.entries.filter((e) => matchesSearch(e.label, term)) : group.entries,
      }))
      .filter((group) => group.entries.length);
  });

  protected readonly options = computed(() => this.groups().flatMap((group) => group.entries));
  protected readonly virtual = computed(
    () => !this.grouped() && this.options().length > VIRTUAL_FROM,
  );
  protected readonly viewportHeight = computed(() =>
    Math.min(this.options().length * OPTION_HEIGHT, this.maxListHeight()),
  );
  protected readonly activeId = computed(() =>
    this.isOpen() ? (this.options()[this.activeIndex()]?.id ?? null) : null,
  );
  protected readonly inputText = computed(() => this.search() ?? this.selectedEntry()?.label ?? '');
  protected readonly showPlaceholder = computed(
    () => !!this.placeholder() && !this.selectedEntry() && !this.search(),
  );

  private readonly overlay = inject(Overlay);
  private readonly viewContainer = inject(ViewContainerRef);
  private readonly injector = inject(Injector);
  private readonly document = inject(DOCUMENT);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly field = viewChild.required<ElementRef<HTMLInputElement>>('field');
  private readonly panel = viewChild.required<TemplateRef<unknown>>('panel');
  private readonly viewport = viewChild(CdkVirtualScrollViewport);
  private overlayRef: OverlayRef | null = null;

  protected readonly trackEntry = (_: number, entry: Entry) => entry.id;

  ngOnDestroy(): void {
    this.close();
  }

  /** Deja la lista sin valor, sin avisar (como `reset()` de DevExtreme tras «Mover a…»). */
  reset(): void {
    this.selected.set(null);
    this.search.set(null);
  }

  protected onFieldClick(): void {
    if (this.searchable()) this.open();
    else this.toggle();
  }

  protected toggle(): void {
    if (this.isOpen()) this.close();
    else {
      this.field().nativeElement.focus();
      this.open();
    }
  }

  protected onType(): void {
    const text = this.field().nativeElement.value;
    this.search.set(text);
    this.open();
    this.showActive.set(true);
    this.emptied.set(text === '');
    // Buscando, la primera que coincide queda enfocada (Enter la elige).
    this.activeIndex.set(text ? this.firstEnabled(1, -1) : this.indexOfSelected());
    this.reveal();
  }

  protected onKeydown(event: KeyboardEvent): void {
    const open = this.isOpen();
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        event.preventDefault();
        const direction = event.key === 'ArrowDown' ? 1 : -1;
        if (event.altKey) {
          if (direction === 1 && !open) this.open();
          else this.close();
        } else if (open) {
          this.showActive.set(true);
          this.emptied.set(false);
          this.activeIndex.set(this.firstEnabled(direction, this.activeIndex()));
          this.reveal();
        } else {
          // Cerrada, como la de DevExtreme: la anterior o la siguiente pasa a ser el valor.
          const next = this.options()[this.firstEnabled(direction, this.indexOfSelected())];
          if (next) this.setValue(next.value);
        }
        return;
      }
      case 'Enter':
        if (!open) return;
        event.preventDefault();
        if (!this.clearIfEmptied()) this.chooseActive();
        return;
      case 'Escape':
        if (!open) return;
        // Atendido: la ventana de atrás (sc-dialog) no se cierra con este Escape.
        event.preventDefault();
        this.close();
        return;
      case 'Tab':
        if (open && !this.clearIfEmptied()) this.chooseActive();
        return;
    }
  }

  protected choose(entry: Entry): void {
    if (entry.disabled) return;
    this.setValue(entry.value);
    this.close();
  }

  protected clear(): void {
    this.setValue(null);
    this.close();
  }

  protected onBlur(): void {
    if (!this.clearIfEmptied()) this.close();
  }

  /**
   * Con búsqueda y limpiable (con ×), borrar el texto y salir deja la lista sin valor, como
   * DevExtreme (allowClearing). En una obligatoria (sin ×) el texto vuelve al del valor.
   */
  private clearIfEmptied(): boolean {
    if (!this.isOpen() || !this.emptied() || !this.searchable() || !this.clearable()) return false;
    this.setValue(null);
    this.close();
    return true;
  }

  private chooseActive(): void {
    const entry = this.options()[this.activeIndex()];
    if (entry && !entry.disabled) this.choose(entry);
    else this.close();
  }

  private setValue(value: any): void {
    if (value === this.selected()) return;
    this.selected.set(value);
    this.valueChange.emit(value);
  }

  private open(): void {
    if (this.isOpen() || this.disabled()) return;
    const host = this.host.nativeElement;
    const view = this.document.defaultView;
    // Como DevExtreme: el panel mide a lo más la mitad de la ventana.
    const maxHeight = Math.floor((view?.innerHeight ?? 900) / 2);
    this.maxListHeight.set(maxHeight - PANEL_CHROME);
    this.activeIndex.set(this.indexOfSelected());
    this.showActive.set(false);
    this.isOpen.set(true);

    const strategy = this.overlay
      .position()
      .flexibleConnectedTo(host)
      .withPositions([
        { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
        { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
      ])
      .withFlexibleDimensions(false)
      .withPush(false);
    const ref = this.overlay.create({
      positionStrategy: strategy,
      scrollStrategy: this.overlay.scrollStrategies.noop(),
      width: host.getBoundingClientRect().width,
      panelClass: 'sck-select-pane',
      // Como sc-dialog: fuera de la capa superior del navegador, en el orden de las capas del CDK.
      usePopover: false,
    });
    strategy.positionChanges.subscribe((change) => {
      this.above.set(change.connectionPair.overlayY === 'bottom');
      snapToPixels(ref.overlayElement);
    });
    ref.attach(new TemplatePortal(this.panel(), this.viewContainer));
    snapToPixels(ref.overlayElement);
    ref.outsidePointerEvents().subscribe((event) => {
      if (!host.contains(event.target as Node)) this.close();
    });
    this.overlayRef = ref;
    this.document.addEventListener('scroll', this.onScroll, true);
    this.reveal();
  }

  close(): void {
    if (!this.isOpen()) return;
    this.isOpen.set(false);
    this.search.set(null);
    this.emptied.set(false);
    // El texto vuelve al del valor también en el DOM: si no hubo una detección de cambios entre lo
    // escrito y el cierre, el enlace [value] no vería cambio y dejaría lo escrito.
    this.field().nativeElement.value = this.inputText();
    this.activeIndex.set(-1);
    this.above.set(false);
    this.document.removeEventListener('scroll', this.onScroll, true);
    this.overlayRef?.dispose();
    this.overlayRef = null;
  }

  /** El scroll de la página o de un contenedor del campo cierra la lista (el de la lista, no). */
  private readonly onScroll = (event: Event) => {
    const target = event.target;
    if (!(target instanceof Node) || this.overlayRef?.overlayElement.contains(target)) return;
    if (target === this.document || target.contains(this.host.nativeElement)) this.close();
  };

  /**
   * La opción enfocada (o la elegida, al abrir) queda a la vista. Antes, como DevExtreme, el panel
   * se vuelve a ubicar: una búsqueda que achica la lista la deja abajo del campo si ahora cabe.
   */
  private reveal(): void {
    afterNextRender(
      () => {
        if (this.overlayRef) {
          this.overlayRef.updatePosition();
          snapToPixels(this.overlayRef.overlayElement);
        }
        const index = this.activeIndex();
        if (index < 0 || !this.overlayRef) return;
        const viewport = this.viewport();
        if (viewport) {
          viewport.scrollToIndex(index);
          return;
        }
        const id = this.options()[index]?.id;
        const element = id ? this.overlayRef.overlayElement.querySelector(`#${id}`) : null;
        element?.scrollIntoView({ block: 'nearest' });
      },
      { injector: this.injector },
    );
  }

  private indexOfSelected(): number {
    const selected = this.selectedEntry();
    return selected ? this.options().indexOf(selected) : -1;
  }

  /** La siguiente opción que se puede elegir en esa dirección, o la misma si no hay otra. */
  private firstEnabled(direction: 1 | -1, from: number): number {
    const options = this.options();
    let index = from < 0 && direction === -1 ? options.length : from;
    for (;;) {
      index += direction;
      if (index < 0 || index >= options.length) return from;
      if (!options[index].disabled) return index;
    }
  }

  private valueOf(item: any): any {
    const field = this.valueField();
    return field && item !== null && typeof item === 'object' ? item[field] : item;
  }

  private labelOf(item: any): string {
    const label = this.label();
    if (label) return label(item);
    const field = this.labelField();
    if (field && item !== null && typeof item === 'object') return String(item[field] ?? '');
    return item === null || item === undefined ? '' : String(item);
  }
}
